import type { QueryClient } from '@tanstack/react-query';

/**
 * Generate cache key for query hooks
 */
export const getQueryCacheKey = (
  cacheKeyPrefix?: string | string[],
  cacheKey?: string | string[],
  params?: null | object,
  cacheKeySuffix?: string,
  space?: string,
) => {
  const key = [];
  if (cacheKeyPrefix)
    key.push(
      ...(Array.isArray(cacheKeyPrefix) ? cacheKeyPrefix : [cacheKeyPrefix]),
    );
  if (cacheKey) key.push(...(Array.isArray(cacheKey) ? cacheKey : [cacheKey]));
  if (params && Object.keys(params).length) key.push(params);
  if (cacheKeySuffix) key.push(cacheKeySuffix);
  if (space) key.push(space);
  return key;
};

const objectHasKey = (
  object: unknown,
  key: string,
): object is { [key]: string } =>
  !!object && typeof object === 'object' && key in object;

/**
 * Invalidate content types queries.
 * If no content type provided, queries for all content types will be invalidated.
 */
export const invalidateContentTypes = (
  queryClient: QueryClient,
  contentTypeName = '',
) =>
  queryClient.invalidateQueries({
    queryKey: ['content-types'],
    predicate: ({ queryKey }) => {
      if (!contentTypeName) return true;

      const isList = !!queryKey.find((query) => query === 'list');
      const params = queryKey.find(
        (key) => objectHasKey(key, 'contentTypeName') && !!key.contentTypeName,
      );
      return (
        isList ||
        (objectHasKey(params, 'contentTypeName') &&
          params?.contentTypeName === contentTypeName)
      );
    },
  });

/**
 * Invalidate content object queries.
 * If no content type provided, queries for all objects types will be invalidated.
 * If no id provided, queries for all objects for provided type will be invalidated.
 */
export const invalidateContentObjects = (
  queryClient: QueryClient,
  contentTypeName = '',
  id = '',
) =>
  queryClient.invalidateQueries({
    queryKey: ['content-objects'],
    predicate: ({ queryKey }) => {
      if (!contentTypeName) return true;

      const isList = !!queryKey.find((query) => query === 'list');
      const params = queryKey.find(
        (key) => objectHasKey(key, 'contentTypeName') && !!key.contentTypeName,
      );
      return (
        objectHasKey(params, 'contentTypeName') &&
        params?.contentTypeName === contentTypeName &&
        (isList || !id || (objectHasKey(params, 'id') && params?.id === id))
      );
    },
  });
